import { useCallback, useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";

import { ErrorBoundary } from "@/components/common/ErrorBoundary";

import { HubSidebar } from "./HubSidebar";
import { HubTopBar } from "./HubTopBar";
import { Footer } from "./Footer";

import "./HubSidebarLayout.css";

const STORAGE_KEY = "hub.sidebar.collapsed";

function readPersistedCollapsed(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function persistCollapsed(value: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(value));
  } catch {
    // Silent failure — localStorage may be unavailable (private mode, quota, ...)
  }
}

/**
 * HubSidebarLayout — primary authenticated shell for admin.
 *
 * Desktop: CSS grid [sidebar | main-column]
 * Mobile:  full-width main with sidebar as overlay drawer
 */
export function HubSidebarLayout(): JSX.Element {
  const [collapsed, setCollapsed] = useState<boolean>(readPersistedCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Close mobile drawer on route change.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Close mobile drawer on Escape key.
  useEffect(() => {
    if (!mobileOpen) return;

    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setMobileOpen(false);
    };

    window.addEventListener("keydown", handleKeyDown);
    return (): void => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [mobileOpen]);

  const handleToggleCollapse = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      persistCollapsed(next);
      return next;
    });
  }, []);

  const handleOpenMobileDrawer = useCallback(() => {
    setMobileOpen(true);
  }, []);

  const handleNavClick = useCallback(() => {
    setMobileOpen(false);
  }, []);

  const handleBackdropClick = useCallback(() => {
    setMobileOpen(false);
  }, []);

  return (
    <div
      className={[
        "hub-sidebar-layout",
        collapsed ? "hub-sidebar-layout--collapsed" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {/* Skip link — mirrors AppShell so keyboard users can bypass the shell
          chrome (sidebar + top bar) on every authenticated page. */}
      <a href="#main-content" className="skip-nav">
        Skip to main content
      </a>

      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="hub-sidebar-layout__backdrop"
          onClick={handleBackdropClick}
          aria-hidden="true"
        />
      )}

      {/* Sidebar (carries an id so the HubTopBar hamburger can aria-controls it). */}
      <HubSidebar
        id="hub-sidebar-drawer"
        collapsed={collapsed}
        onToggleCollapse={handleToggleCollapse}
        mobileOpen={mobileOpen}
        onNavClick={handleNavClick}
      />

      {/* Right column: top bar + scrollable content + footer */}
      <div className="hub-sidebar-layout__main">
        <HubTopBar
          onOpenMobileDrawer={handleOpenMobileDrawer}
          mobileDrawerOpen={mobileOpen}
          mobileDrawerId="hub-sidebar-drawer"
        />

        <main
          id="main-content"
          role="main"
          className="hub-sidebar-layout__content"
        >
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>

        <Footer />
      </div>
    </div>
  );
}
